import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type PropsWithChildren,
} from 'react';

import type { AnalyzeQuotaInfo } from '../components/billing/AnalyzeQuotaModal';
import { useJoinInviteGate } from '../components/household/JoinInviteListener';
import { navigateToAuth } from '../core/navigation/navigationRef';
import { waitForLoginNotificationPrompt } from '../services/loginNotificationPromptGate';
import { useImportFlow } from '../modules/ImportOnboarding/hooks/useImportFlow';
import type {
  ImportCategoryBreakdownItem,
  ImportLocalImage,
  ImportMonthSection,
  ImportProgress,
} from '../types/import';
import { telemetry } from '../services/telemetry';
import { useAuth } from './AuthContext';

type ImportOnboardingContextValue = {
  /** Sheet visibility — false while minimized mid-import. */
  isModalVisible: boolean;
  /** Auto-shown for authenticated users who have not finished import onboarding. */
  open: () => void;
  /** Re-open for “Import more” / progress chip without resetting onboardingComplete. */
  openImportMore: () => void;
  /**
   * Dismiss the sheet. While upload/analyze is in flight, this minimizes
   * (hides UI, keeps orchestration). Otherwise hides and session-dismisses onboarding.
   * Minimizing never cancels the batch or blocks onboardingComplete on success.
   */
  close: () => void;
  /** Hide the sheet without disposing an in-flight import. */
  minimize: () => void;

  images: ImportLocalImage[];
  progress: ImportProgress;
  busy: boolean;
  categoryBreakdown: ImportCategoryBreakdownItem[];
  monthSections: ImportMonthSection[];
  remainingAnalyzes?: number;
  analyzeLimit?: number;
  quotaModal: AnalyzeQuotaInfo | null;
  dismissQuotaModal: () => void;
  addImages: (next: ImportLocalImage[]) => void;
  removeImage: (id: string) => void;
  beginImport: () => Promise<void>;
  reset: () => void;
  skipOnboarding: () => Promise<void>;
};

const ImportOnboardingContext = createContext<ImportOnboardingContextValue | null>(null);

export function ImportOnboardingProvider({ children }: PropsWithChildren) {
  const { isAuthenticated, isLoading, user } = useAuth();
  const { isJoinInviteBlocking } = useJoinInviteGate();
  const {
    images,
    progress,
    busy,
    categoryBreakdown,
    monthSections,
    remainingAnalyzes,
    analyzeLimit,
    quotaModal,
    dismissQuotaModal,
    addImages,
    removeImage,
    beginImport,
    reset,
    skipOnboarding,
  } = useImportFlow();
  const [isModalVisible, setIsModalVisible] = useState(false);
  /** Keeps auto-open from fighting minimize / success-step display. */
  const [heldOpen, setHeldOpen] = useState(false);
  const [dismissedForSession, setDismissedForSession] = useState(false);

  const needsImportOnboarding =
    isAuthenticated && !isLoading && !!user && user.onboardingComplete !== true;

  useEffect(() => {
    if (!isAuthenticated) {
      setIsModalVisible(false);
      setHeldOpen(false);
      setDismissedForSession(false);
      reset();
    }
  }, [isAuthenticated, reset]);

  useEffect(() => {
    if (
      needsImportOnboarding &&
      !dismissedForSession &&
      !heldOpen &&
      !isJoinInviteBlocking
    ) {
      let cancelled = false;
      void waitForLoginNotificationPrompt().then(() => {
        if (cancelled) return;
        setIsModalVisible(true);
        setHeldOpen(true);
        void telemetry.logFeature('import_open', { entry: 'onboarding_auto' });
      });
      return () => {
        cancelled = true;
      };
    }
    return undefined;
  }, [dismissedForSession, heldOpen, isJoinInviteBlocking, needsImportOnboarding]);

  const open = useCallback(() => {
    if (!isAuthenticated) {
      navigateToAuth();
      return;
    }
    setDismissedForSession(false);
    setHeldOpen(true);
    setIsModalVisible(true);
    void telemetry.logFeature('import_open', { entry: 'onboarding' });
  }, [isAuthenticated]);

  const openImportMore = useCallback(() => {
    if (!isAuthenticated) {
      navigateToAuth();
      return;
    }
    setHeldOpen(true);
    setIsModalVisible(true);
    void telemetry.logFeature('import_open', { entry: 'import_more' });
  }, [isAuthenticated]);

  const minimize = useCallback(() => {
    setIsModalVisible(false);
    // Keep heldOpen so auto-open effect does not immediately re-show the sheet.
    setHeldOpen(true);
  }, []);

  const close = useCallback(() => {
    if (busy) {
      minimize();
      return;
    }
    setIsModalVisible(false);
    setHeldOpen(false);
    if (needsImportOnboarding) {
      setDismissedForSession(true);
    }
  }, [busy, minimize, needsImportOnboarding]);

  const value = useMemo(
    (): ImportOnboardingContextValue => ({
      isModalVisible,
      open,
      openImportMore,
      close,
      minimize,
      images,
      progress,
      busy,
      categoryBreakdown,
      monthSections,
      remainingAnalyzes,
      analyzeLimit,
      quotaModal,
      dismissQuotaModal,
      addImages,
      removeImage,
      beginImport,
      reset,
      skipOnboarding,
    }),
    [
      addImages,
      analyzeLimit,
      beginImport,
      busy,
      categoryBreakdown,
      close,
      dismissQuotaModal,
      images,
      isModalVisible,
      minimize,
      monthSections,
      open,
      openImportMore,
      progress,
      quotaModal,
      remainingAnalyzes,
      removeImage,
      reset,
      skipOnboarding,
    ],
  );

  return (
    <ImportOnboardingContext.Provider value={value}>{children}</ImportOnboardingContext.Provider>
  );
}

export function useImportOnboarding(): ImportOnboardingContextValue {
  const ctx = useContext(ImportOnboardingContext);
  if (!ctx) {
    throw new Error('useImportOnboarding must be used within ImportOnboardingProvider');
  }
  return ctx;
}
